import { useNavigate } from '@tanstack/react-router';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/common/data-table/data-table';

import {
  MOCK_CUSTOMERS,
  calculateAgingTotals,
  getAgingPercentages,
  type ARCustomer,
} from '../data/mockARData';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCurrencyK(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
}

const BUCKET_COLORS = {
  current: '#22c55e', // green
  days30: '#84cc16', // lime
  days60: '#eab308', // yellow
  days90: '#f97316', // orange
  over90: '#ef4444', // red
};

interface AgingBucketCardProps {
  label: string;
  amount: number;
  percentage: string;
  color: string;
}

function AgingBucketCard({
  label,
  amount,
  percentage,
  color,
}: AgingBucketCardProps) {
  return (
    <Card className="flex-1">
      <CardContent className="pt-4 pb-4">
        <div className="text-sm text-muted-foreground mb-1">{label}</div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold" style={{ color }}>
            {formatCurrencyK(amount)}
          </span>
          <span className="text-sm text-muted-foreground">{percentage}%</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function ARAgingReportPage() {
  const navigate = useNavigate();
  const totals = calculateAgingTotals();
  const percentages = getAgingPercentages();

  const chartData = [
    { name: 'Current', value: totals.current, color: BUCKET_COLORS.current },
    { name: '1-30', value: totals.days30, color: BUCKET_COLORS.days30 },
    { name: '31-60', value: totals.days60, color: BUCKET_COLORS.days60 },
    { name: '61-90', value: totals.days90, color: BUCKET_COLORS.days90 },
    { name: '90+', value: totals.over90, color: BUCKET_COLORS.over90 },
  ];

  const handleRowClick = (customer: ARCustomer) => {
    navigate({
      to: '/ar/collections/$customerId',
      params: { customerId: customer.id },
    });
  };

  const columns: ColumnDef<ARCustomer>[] = [
    {
      accessorKey: 'name',
      header: 'Customer',
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue<string>()}</span>
      ),
    },
    {
      accessorKey: 'paymentTerms',
      header: 'Payment Terms',
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">{getValue<string>()}</span>
      ),
    },
    {
      id: 'current',
      header: 'Current',
      accessorFn: (row) => row.aging.current,
      cell: ({ getValue }) => (
        <span style={{ color: BUCKET_COLORS.current }}>
          {formatCurrency(getValue<number>())}
        </span>
      ),
    },
    {
      id: 'days30',
      header: '1-30 Days',
      accessorFn: (row) => row.aging.days30,
      cell: ({ getValue }) => (
        <span style={{ color: BUCKET_COLORS.days30 }}>
          {formatCurrency(getValue<number>())}
        </span>
      ),
    },
    {
      id: 'days60',
      header: '31-60 Days',
      accessorFn: (row) => row.aging.days60,
      cell: ({ getValue }) => (
        <span style={{ color: BUCKET_COLORS.days60 }}>
          {formatCurrency(getValue<number>())}
        </span>
      ),
    },
    {
      id: 'days90',
      header: '61-90 Days',
      accessorFn: (row) => row.aging.days90,
      cell: ({ getValue }) => (
        <span style={{ color: BUCKET_COLORS.days90 }}>
          {formatCurrency(getValue<number>())}
        </span>
      ),
    },
  ];

  const table = useReactTable({
    columns,
    data: MOCK_CUSTOMERS,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Page title="AR Aging Report">
      <div className="space-y-6">
        {/* Header with total */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">AR Aging Report</h1>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              Total Outstanding
            </div>
            <div className="text-2xl font-bold">
              {formatCurrency(totals.total)}
            </div>
          </div>
        </div>

        {/* Aging Bucket Cards */}
        <div className="flex gap-4">
          <AgingBucketCard
            label="Current"
            amount={totals.current}
            percentage={percentages.current}
            color={BUCKET_COLORS.current}
          />
          <AgingBucketCard
            label="1-30"
            amount={totals.days30}
            percentage={percentages.days30}
            color={BUCKET_COLORS.days30}
          />
          <AgingBucketCard
            label="31-60"
            amount={totals.days60}
            percentage={percentages.days60}
            color={BUCKET_COLORS.days60}
          />
          <AgingBucketCard
            label="61-90"
            amount={totals.days90}
            percentage={percentages.days90}
            color={BUCKET_COLORS.days90}
          />
          <AgingBucketCard
            label="90+"
            amount={totals.over90}
            percentage={percentages.over90}
            color={BUCKET_COLORS.over90}
          />
        </div>

        {/* Chart and Table row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Aging Summary Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Aging Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${(value / 1000).toFixed(0)}K`
                      }
                    />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Aging Details by Customer */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Aging Details by Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <DataTable table={table} onRowClick={handleRowClick} />
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}

export default ARAgingReportPage;
